import { Alert } from "@gigpro/ui/Alert";
import { Button } from "@gigpro/ui/Button";
import { Dialog, DialogFooter, DialogTitle } from "@gigpro/ui/Dialog";
import { Form } from "@gigpro/ui/Form";
import { Checkbox } from "@gigpro/ui/Form/Checkbox";
import { DateField } from "@gigpro/ui/Form/DateField";
import { MaskedTextfield, wageMaskProps } from "@gigpro/ui/Form/MaskedTextfield";
import { Textfield } from "@gigpro/ui/Form/Textfield";
import { TimeField } from "@gigpro/ui/Form/TimeField";
import { useZodForm } from "@gigpro/ui/Form/utils";
import { Input } from "@gigpro/ui/Input";
import { Label } from "@gigpro/ui/Label";
import { toast } from "@gigpro/ui/Toast";
import { useNow } from "@gigpro/ui/utils/useNow";
import { formatCurrency } from "@gigpro/utils";
import { combineDateAndTime, convertToUtc, nowInTimezone, shortenTimezone } from "@gigpro/utils/datetime";
import { useDebouncedValue } from "@gigpro/utils/hooks/useDebouncedValue";
import { ApiError } from "@sidegigllc/api";
import {
  type AdminCreateBackPayShiftRequest,
  type AdminGigApplication,
  useAdminGigApplicationsRetrieve,
  useLocationsRetrieve,
  useSupportOpsBackPayShiftCreate,
} from "@sidegigllc/api/client";
import { useAdminPro } from "api/queries/admin";
import { EditBreaksDialog } from "domains/admin/containers/EditBreaksDialog";
import { PositionSelect } from "domains/postGig/containers/PositionSelect";
import { proSectionSchema } from "domains/postGig/providers/PostGigData/types";
import React from "react";
import { useFormState, useWatch } from "react-hook-form";
import type { ModalBaseProps } from "types/utils";
import * as z from "zod/mini";

type BackPayProDialogProps = ModalBaseProps<"new"> & { proId: number };

const formSchema = z
  .object({
    location_id: z.number("Enter a location ID").check(z.int(), z.positive("Enter a location ID")),
    pro_id: z.number(),
    position: proSectionSchema.check(z.minLength(1, "Select a position")),
    wage_amount: z.number("Enter a pay rate").check(z.positive("Enter a pay rate")),
    wage_override: z.boolean(),
    shift_date: z.date("Select a shift date"),
    start_time: z.date("Enter a clock in time"),
    end_time: z.date("Enter a clock out time"),
  })
  .check(
    z.superRefine((data, ctx) => {
      const start = combineDateAndTime(data.shift_date, data.start_time);
      const end = combineDateAndTime(data.shift_date, data.end_time);
      if (end <= start) {
        ctx.addIssue({
          code: "custom",
          path: ["end_time"],
          message: "Clock out must be after clock in. Overnight shifts are not supported.",
        });
      }
    }),
  );
type FormValues = z.infer<typeof formSchema>;

const serverFieldMapping = { skill_id: "position" } as const;
const serverFields = ["location_id", "pro_id", "skill_id", "wage_amount", "start_time", "end_time"] as const;

const locationErrorMessage = (error: ApiError) =>
  error.isNotFound() ? "Location not found" : error.getMainErrorMessage();

const toInstant = (date: Date, time: Date, timezone: string) => convertToUtc(combineDateAndTime(date, time), timezone);

export function BackPayProDialog({ open, onOpenChange, proId }: Readonly<BackPayProDialogProps>) {
  const { data: pro } = useAdminPro({ id: proId });
  const proName = pro ? `${pro.first_name} ${pro.last_name}` : "this pro";
  const [created, setCreated] = React.useState<AdminGigApplication>();
  const [wasOpen, setWasOpen] = React.useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) setCreated(undefined);
  }

  const finish = () => {
    toast.success(`Shift created for ${proName} — view it on the Pay page.`);
    onOpenChange(false);
  };

  return (
    <>
      <Dialog open={open && !created} onOpenChange={onOpenChange}>
        <DialogTitle>Back Pay Pro</DialogTitle>
        <DialogBody proId={proId} proName={proName} onCreated={setCreated} onCancel={() => onOpenChange(false)} />
      </Dialog>
      {created && <BreaksStep open={open} application={created} onFinish={finish} />}
    </>
  );
}

function BreaksStep({
  open,
  application,
  onFinish,
}: Readonly<{ open: boolean; application: AdminGigApplication; onFinish: () => void }>) {
  const { data, refetch } = useAdminGigApplicationsRetrieve(application.id, {
    query: { initialData: application, staleTime: Number.POSITIVE_INFINITY },
  });
  return (
    <EditBreaksDialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) onFinish();
      }}
      application={data}
      refetchApplication={() => void refetch()}
      onSuccess={onFinish}
    />
  );
}

type DialogBodyProps = Readonly<{
  proId: number;
  proName: string;
  onCreated: (application: AdminGigApplication) => void;
  onCancel: () => void;
}>;

function DialogBody({ proId, proName, onCreated, onCancel }: DialogBodyProps) {
  const [rejectedWage, setRejectedWage] = React.useState<{ wage: number; message: string }>();

  const form = useZodForm({
    schema: formSchema,
    defaultValues: { pro_id: proId, position: [], wage_override: false },
  });

  const [locationId, position, wage] = useWatch({
    control: form.control,
    name: ["location_id", "position", "wage_amount"],
  });
  const debouncedLocationId = useDebouncedValue(locationId, 400);
  const locationQuery = useLocationsRetrieve(debouncedLocationId, {
    query: { enabled: debouncedLocationId > 0, retry: false },
  });
  const location = locationQuery.data?.id === locationId ? locationQuery.data : undefined;
  const locationError =
    locationQuery.isError && debouncedLocationId === locationId ? ApiError.fromThrown(locationQuery.error) : undefined;
  const timezone = location?.timezone;
  const selectedPosition = position.filter(({ pro }) => pro.location_id === location?.id);

  const now = useNow({ updateIntervalMs: false });
  const [positionShift] = React.useState(() => ({ baseDate: now, startTime: now, endTime: now }));

  const createShift = useSupportOpsBackPayShiftCreate();
  const { isSubmitting, errors } = useFormState({ control: form.control });

  const overrideRequired = rejectedWage?.wage !== undefined && wage === rejectedWage.wage;

  const onSubmit = async (data: FormValues) => {
    if (!location?.timezone) {
      form.setError("location_id", { type: "api", message: "Enter a valid location ID" });
      return;
    }
    if (overrideRequired && !data.wage_override) {
      form.setError("wage_override", { type: "validate", message: "Confirm to pay this rate anyway" });
      return;
    }
    const selected = data.position.find(({ pro }) => pro.location_id === location.id);
    if (!selected) {
      form.setError("position", { type: "validate", message: "Select a position" });
      return;
    }
    const start = toInstant(data.shift_date, data.start_time, location.timezone);
    const end = toInstant(data.shift_date, data.end_time, location.timezone);
    if (end >= new Date()) {
      form.setError("end_time", { type: "validate", message: "Shift must be in the past" });
      return;
    }
    const sendOverride = overrideRequired && data.wage_override;
    try {
      const application = await createShift.mutateAsync({
        data: {
          location_id: data.location_id,
          pro_id: data.pro_id,
          skill_id: selected.pro.skill_id,
          wage_amount: data.wage_amount,
          wage_override: sendOverride,
          start_time: start,
          end_time: end,
        },
      });
      onCreated(application);
    } catch (error) {
      const apiError = ApiError.fromThrown<AdminCreateBackPayShiftRequest>(error);
      if (!serverFields.some((field) => apiError.hasFieldError(field))) {
        form.setError("root.server", { type: "api", message: apiError.getMainErrorMessage() });
        return;
      }
      apiError.setFormErrors(form.setError, serverFieldMapping);
      const wageMessage = apiError.getFieldErrorMessage("wage_amount");
      if (wageMessage && !sendOverride) {
        form.clearErrors("wage_amount");
        form.setValue("wage_override", false);
        setRejectedWage({ wage: data.wage_amount, message: wageMessage });
      }
    }
  };

  return (
    <Form form={form} onSubmit={onSubmit} className="flex flex-col gap-3">
      <p className="text-gray">Manually create a completed shift for {proName}.</p>
      {errors.root?.server?.message && <Alert severity="error">{errors.root.server.message}</Alert>}
      <Textfield
        control={form.control}
        name="location_id"
        label="Location ID"
        placeholder="4213"
        inputMode="numeric"
        transform={{
          input: (value) => (value ? String(value) : ""),
          output: (value) => Number(value.replaceAll(/\D/g, "")),
        }}
        description={locationError ? locationErrorMessage(locationError) : location?.name}
      />
      <Textfield
        control={form.control}
        name="pro_id"
        label="Pro ID"
        disabled
        transform={{ input: String, output: Number }}
      />
      {location ? (
        <PositionSelect
          location={location}
          shift={{ ...positionShift, timezone: location.timezone ?? "" }}
          value={selectedPosition}
          onChange={(selected) => form.setValue("position", selected, { shouldDirty: true, shouldValidate: true })}
          error={errors.position?.message ?? errors.position?.root?.message}
          flags={{ disableDefaults: true, hideWage: true, showAutoSelect: false }}
        />
      ) : (
        <div className="flex flex-col gap-1.5">
          <Label>Position</Label>
          <Input disabled placeholder="Enter a valid location ID first" />
        </div>
      )}
      <div className="grid grid-cols-2 gap-3">
        <MaskedTextfield
          control={form.control}
          name="wage_amount"
          label="Pay Rate"
          placeholder="19.00"
          {...wageMaskProps}
        />
        <DateField
          control={form.control}
          name="shift_date"
          label="Shift Date"
          maxDate={timezone ? nowInTimezone(timezone) : now}
        />
        <TimeField control={form.control} name="start_time" label="Clock In" timezone={shortenTimezone(timezone)} />
        <TimeField control={form.control} name="end_time" label="Clock Out" timezone={shortenTimezone(timezone)} />
      </div>
      {overrideRequired && (
        <Alert severity="warning" className="flex-col items-start">
          <span>
            {rejectedWage.message} The requested rate is {formatCurrency(rejectedWage.wage)}/hr.
          </span>
          <Checkbox control={form.control} name="wage_override" label="Pay this rate anyway" />
        </Alert>
      )}
      <DialogFooter>
        <Button onClick={onCancel} className="w-full" disabled={isSubmitting}>
          Cancel
        </Button>
        <Button type="submit" className="w-full" variant="contained" loading={isSubmitting}>
          Back Pay Pro
        </Button>
      </DialogFooter>
    </Form>
  );
}
